import type {
  AjustesAgenda,
  AjustesNevora,
  EtapaPrevision,
  UmbralesSemaforo,
} from "./tipos";

/** Umbrales del semáforo por defecto (SPEC.md §4): verde ≤2, azul ≤7, amarillo ≤14, naranja ≤30. */
export const UMBRALES_POR_DEFECTO: UmbralesSemaforo = {
  verde: 2,
  azul: 7,
  amarillo: 14,
  naranja: 30,
};

/** Sectores iniciales (pyme de Barcelona, puerta fría). Editables por admin en Ajustes. */
export const SECTORES_POR_DEFECTO: string[] = [
  "Restauración",
  "Comercio",
  "Servicios profesionales",
  "Salud y bienestar",
  "Construcción y reformas",
  "Inmobiliaria",
  "Automoción y talleres",
  "Belleza y estética",
  "Educación y formación",
  "Industria",
  "Otros",
];

/** Motivos iniciales de «No le interesa», sacados de las objeciones de los primeros leads. */
export const MOTIVOS_POR_DEFECTO: string[] = [
  "Ya lo tiene cubierto",
  "Sin presupuesto",
  "No es el momento",
  "No ve la necesidad",
  "Negocio demasiado pequeño",
  "Otro",
];

/** 47 (31/10/2026): motivos tras la consultoría (aún sin propuesta). Editables en Ajustes. */
export const MOTIVOS_CONSULTORIA_POR_DEFECTO: string[] = [
  "No encaja con lo que necesita",
  "Sin presupuesto",
  "No es el momento",
  "Lo hará por su cuenta",
  "Ya trabaja con otro proveedor",
  "Dejó de responder",
  "Otro",
];

/** 47: motivos tras la propuesta. */
export const MOTIVOS_PROPUESTA_POR_DEFECTO: string[] = [
  "Precio",
  "Eligió a otro",
  "Lo hará por su cuenta",
  "No ve claro el retorno",
  "No es el momento",
  "Dejó de responder",
  "Otro",
];

/** 47: probabilidad de cierre de cada etapa abierta (previsión). Editables en Ajustes. */
export const PROBABILIDADES_POR_DEFECTO: Record<EtapaPrevision, number> = {
  consultoria_agendada: 15,
  no_show: 5,
  esperando_info: 25,
  preparando_propuesta: 35,
  propuesta_lista: 40,
  presentacion_agendada: 50,
  pensandolo: 50,
};

export const AJUSTES_POR_DEFECTO: AjustesNevora = {
  semaforo: UMBRALES_POR_DEFECTO,
  maxIntentos: 3,
  sectores: SECTORES_POR_DEFECTO,
  motivosNoInteresa: MOTIVOS_POR_DEFECTO,
  motivosConsultoria: MOTIVOS_CONSULTORIA_POR_DEFECTO,
  motivosPropuesta: MOTIVOS_PROPUESTA_POR_DEFECTO,
  probabilidades: PROBABILIDADES_POR_DEFECTO,
  comisionPct: 5,
  comisionConsultorPct: 10,
  topePendientes: 50,
  diasRecuperacion: 7,
  topeConsultoriasSemana: 5,
  reservasUrl: null,
  limiteSinAsignar: 20,
  diasFrio: 7,
  pushMinutosAntes: 15,
  responsableWeb: null,
  responsableConsultoria: null,
  agenda: {
    propia: false,
    propiaDesde: null,
    duracionMin: 30,
    bloqueMin: 60,
    pasoMin: 30,
    antelacionHoras: 2,
    dias: 14,
    prioridad: "admins",
    recordatorioHoras: 3,
  },
};

/** Id de un usuario (sales) guardado en Ajustes; cualquier otra cosa, ninguno. */
const idUsuario = (v: unknown): number | null =>
  typeof v === "number" && Number.isInteger(v) && v > 0 ? v : null;

const entero = (v: unknown, porDefecto: number) =>
  typeof v === "number" && Number.isFinite(v) && v >= 0
    ? Math.floor(v)
    : porDefecto;

/** Porcentaje entre 0 y 100 con decimales (la comisión puede ser 5,5 %). */
const porcentaje = (v: unknown, porDefecto: number) =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100
    ? v
    : porDefecto;

/** Enlace http(s) o null. */
export const enlaceValido = (v: unknown): string | null =>
  typeof v === "string" && /^https?:\/\/\S+$/i.test(v.trim()) ? v.trim() : null;

/**
 * Lee los ajustes Nevora del jsonb `configuration.config.nevora`, rellenando lo que falte
 * con los valores por defecto. Nunca lanza: un valor corrupto vuelve al defecto.
 */
export const leerAjustes = (config: unknown): AjustesNevora => {
  const raiz =
    config && typeof config === "object"
      ? ((config as Record<string, unknown>).nevora as
          | Record<string, unknown>
          | undefined)
      : undefined;
  const sem = (raiz?.semaforo ?? {}) as Record<string, unknown>;
  const semaforo: UmbralesSemaforo = {
    verde: entero(sem.verde, UMBRALES_POR_DEFECTO.verde),
    azul: entero(sem.azul, UMBRALES_POR_DEFECTO.azul),
    amarillo: entero(sem.amarillo, UMBRALES_POR_DEFECTO.amarillo),
    naranja: entero(sem.naranja, UMBRALES_POR_DEFECTO.naranja),
  };
  const lista = (v: unknown, porDefecto: string[]) => {
    if (!Array.isArray(v)) return porDefecto;
    const limpia = (v as unknown[])
      .filter((s): s is string => typeof s === "string" && s.trim() !== "")
      .map((s) => s.trim());
    return limpia.length ? limpia : porDefecto;
  };
  const sectores = lista(raiz?.sectores, SECTORES_POR_DEFECTO);
  return {
    semaforo,
    maxIntentos: Math.max(1, entero(raiz?.maxIntentos, 3)),
    sectores,
    motivosNoInteresa: lista(raiz?.motivosNoInteresa, MOTIVOS_POR_DEFECTO),
    motivosConsultoria: lista(
      raiz?.motivosConsultoria,
      MOTIVOS_CONSULTORIA_POR_DEFECTO,
    ),
    motivosPropuesta: lista(
      raiz?.motivosPropuesta,
      MOTIVOS_PROPUESTA_POR_DEFECTO,
    ),
    probabilidades: leerProbabilidades(raiz?.probabilidades),
    comisionPct: porcentaje(raiz?.comisionPct, 5),
    comisionConsultorPct: porcentaje(raiz?.comisionConsultorPct, 10),
    topePendientes: entero(raiz?.topePendientes, 50),
    diasRecuperacion: Math.max(1, entero(raiz?.diasRecuperacion, 7)),
    topeConsultoriasSemana: Math.max(
      1,
      entero(raiz?.topeConsultoriasSemana, 5),
    ),
    reservasUrl: enlaceValido(raiz?.reservasUrl),
    limiteSinAsignar: entero(raiz?.limiteSinAsignar, 20),
    diasFrio: Math.max(1, entero(raiz?.diasFrio, 7)),
    pushMinutosAntes: Math.min(
      240,
      Math.max(1, entero(raiz?.pushMinutosAntes, 15)),
    ),
    responsableWeb: idUsuario(raiz?.responsableWeb),
    responsableConsultoria: idUsuario(raiz?.responsableConsultoria),
    agenda: leerAgenda(raiz?.agenda),
  };
};

/** 47: una probabilidad por etapa abierta (0-100); lo que falte o no valga, la de por defecto. */
const leerProbabilidades = (v: unknown): Record<EtapaPrevision, number> => {
  const p = (v ?? {}) as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(PROBABILIDADES_POR_DEFECTO).map(([etapa, porDefecto]) => [
      etapa,
      porcentaje(p[etapa], porDefecto),
    ]),
  ) as Record<EtapaPrevision, number>;
};

/** 41: el calendario de consultorías, con los mismos límites que la base (nevora_agenda_cfg). */
const leerAgenda = (v: unknown): AjustesAgenda => {
  const a = (v ?? {}) as Record<string, unknown>;
  const entre = (x: unknown, porDefecto: number, min: number, max: number) =>
    Math.min(max, Math.max(min, entero(x, porDefecto)));
  return {
    propia: a.propia === true,
    propiaDesde:
      typeof a.propiaDesde === "string" &&
      !Number.isNaN(Date.parse(a.propiaDesde))
        ? a.propiaDesde
        : null,
    duracionMin: entre(a.duracionMin, 30, 5, 240),
    bloqueMin: entre(a.bloqueMin, 60, 15, 480),
    pasoMin: entre(a.pasoMin, 30, 5, 120),
    antelacionHoras: entre(a.antelacionHoras, 2, 0, 72),
    dias: entre(a.dias, 14, 1, 60),
    prioridad: a.prioridad === "consultores" ? "consultores" : "admins",
    recordatorioHoras: entre(a.recordatorioHoras, 3, 1, 24),
  };
};

/** Umbrales válidos: enteros no negativos y estrictamente crecientes. */
export const umbralesValidos = (u: UmbralesSemaforo) =>
  [u.verde, u.azul, u.amarillo, u.naranja].every(
    (n) => Number.isInteger(n) && n >= 0,
  ) &&
  u.verde < u.azul &&
  u.azul < u.amarillo &&
  u.amarillo < u.naranja;

/**
 * Devuelve el jsonb `config` completo con los ajustes Nevora encima de los que ya había:
 * una clave que esta versión de la app no conozca no se pierde al guardar.
 */
export const escribirAjustes = (
  config: Record<string, unknown> | undefined,
  ajustes: AjustesNevora,
): Record<string, unknown> => ({
  ...(config ?? {}),
  nevora: {
    ...(((config ?? {}).nevora as Record<string, unknown> | undefined) ?? {}),
    ...ajustes,
  },
});
